import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import {
  LogOut, Package, Store, Shield, Wallet, Sparkles, ArrowDownToLine, ArrowUpFromLine,
  Heart, Film, Radio, Users, Save, Plus, Trash2, X, Edit2, Check, ChevronRight, Crown, MapPin, User as UserIcon
} from "lucide-react";
import PlansTab from "../components/PlansTab";
import AddressBook from "../components/AddressBook";
import { api, formatApiError } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { AddressFields, PhoneField, IdProofField, useFieldPolicy, policyFor } from "../components/ProfileFields";

const RELATIONS = ["self", "mom", "dad", "spouse", "son", "daughter", "sibling", "friend", "other"];
const GENDERS = [
  { id: "male", label: "Male" },
  { id: "female", label: "Female" },
  { id: "non_binary", label: "Non-binary" },
  { id: "prefer_not", label: "Prefer not to say" },
];

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [schema, setSchema] = useState(null);
  const [tab, setTab] = useState("overview");
  const [searchParams, setSearchParams] = useSearchParams();
  // Sync `?tab=preferences` → profile tab, keep URL in step when the user
  // switches tabs via the UI so deep-links stay shareable.
  useEffect(() => {
    const q = searchParams.get("tab");
    if (q && q !== tab) setTab(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    if (!tab) return;
    if (searchParams.get("tab") !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  const [prefOwnerOverride, setPrefOwnerOverride] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const flash = (m, isErr = false) => {
    if (isErr) { setErr(m); setMsg(""); } else { setMsg(m); setErr(""); }
    setTimeout(() => { setMsg(""); setErr(""); }, 2400);
  };

  const reload = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/buyer/profile"),
        api.get("/buyer/preference-schema"),
      ]);
      setProfile(p.data);
      setSchema(s.data);
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Load failed", true); }
  };

  useEffect(() => { if (user) reload(); }, [user]);

  if (!user) {
    return <div className="max-w-md mx-auto p-10 text-center">
      <div className="font-display text-2xl mb-4">Not signed in</div>
      <Link to="/login" className="btn-primary">Login</Link>
      <Link to="/register" className="btn-secondary ml-2">Sign up</Link>
    </div>;
  }
  if (!profile) return <div className="p-10 text-center text-[#A1A1AA]">Loading…</div>;

  // Hide shopper-only tabs (Saved / Preferences / Family) for admins and
  // sellers. They land on the Overview tab and only see profile editing.
  const isShopper = !user || user.role === "buyer";
  const TABS = [
    ["overview", "Overview", UserIcon],
    ...(isShopper ? [
      ["addresses", "Addresses", MapPin],
      ["saved", "Saved", Heart],
      ["preferences", "Preferences", Sparkles],
      ["membership", "Membership", Crown],
      ["family", "Family", Users],
    ] : []),
  ];

  return (
    <div className="pb-24 md:pb-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <h1 className="font-display font-black text-4xl sm:text-6xl uppercase mb-6">My profile<span className="text-[#D4FF00]">.</span></h1>

      {/* Header card */}
      <div className="card p-5 flex items-center gap-4 mb-4">
        <img src={profile.picture} className="w-16 h-16 rounded-2xl" alt=""/>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-2xl truncate">{profile.name || "Welcome"}</div>
          <div className="text-sm text-[#A1A1AA] truncate">{profile.email}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="chip active text-xs">{profile.role}</span>
            {isShopper && profile.active_shopper && profile.active_shopper !== "self" && (
              <ActiveShopperPill profile={profile} onChange={reload}/>
            )}
            {isShopper && (!profile.active_shopper || profile.active_shopper === "self") && profile.family_members.length > 0 && (
              <ActiveShopperPill profile={profile} onChange={reload}/>
            )}
          </div>
        </div>
        {isShopper && (
          <Link to="/orders" className="btn-secondary text-xs px-3 py-2 hidden sm:inline-flex" data-testid="profile-orders-cta">
            <Package size={14} className="inline mr-1"/> Orders
          </Link>
        )}
      </div>

      {/* Tabs — only render the strip if we have multiple tabs (i.e. shoppers).
          Admins / sellers go straight to the Overview body. */}
      {TABS.length > 1 && (
        <div className="sticky top-[64px] z-30 -mx-4 px-4 bg-[#0F0817]/95 backdrop-blur border-b border-[#3D2845] mb-6">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map(([k, l, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`profile-tab-${k}`}
                className={`shrink-0 px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 ${tab === k ? "border-[#D4FF00] text-[#D4FF00]" : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA]"}`}>
                <Icon size={14}/> {l}
              </button>
            ))}
          </div>
        </div>
      )}

      {msg && <div className="mb-4 text-sm text-[#D4FF00]" data-testid="profile-msg">{msg}</div>}
      {err && <div className="mb-4 text-sm text-[#FF007F]" data-testid="profile-err">{err}</div>}

      {tab === "overview" && (
        <OverviewTab profile={profile} onUpdated={(m) => { flash(m); reload(); }}
          onError={(m) => flash(m, true)} onLogout={logout}/>
      )}
      {tab === "saved" && (
        <SavedTab/>
      )}
      {tab === "preferences" && schema && (
        <PreferencesTab profile={profile} schema={schema}
          initialOwner={prefOwnerOverride}
          onUpdated={(m) => { flash(m); reload(); }}
          onError={(m) => flash(m, true)}/>
      )}
      {tab === "membership" && (
        <PlansTab audience="buyer" flash={(m, isErr) => flash(m, isErr)}/>
      )}
      {tab === "addresses" && <AddressBook/>}
      {tab === "family" && (
        <FamilyTab profile={profile}
          onUpdated={(m) => { flash(m); reload(); }}
          onError={(m) => flash(m, true)}
          onPrefer={(memberId) => { setPrefOwnerOverride(memberId); setTab("preferences"); }}/>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
function ActiveShopperPill({ profile, onChange }) {
  const [open, setOpen] = useState(false);
  const cur = profile.active_shopper || "self";
  const curMember = profile.family_members.find(m => m.member_id === cur);
  const curLabel = cur === "self" ? "Shopping for: You" : `Shopping for: ${curMember?.name || "Member"}`;
  const set = async (target) => {
    try {
      await api.put("/buyer/active-shopper", { owner: target });
      setOpen(false);
      onChange();
    } catch (e) {
      // Surface the failure so the buyer knows the switch didn't take.
      console.error("[profile] active-shopper switch failed", e);
      alert(e?.response?.data?.detail || "Couldn't switch shopper. Try again.");
    }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} data-testid="active-shopper-pill"
        className="chip text-xs gap-1">
        <Users size={12}/> {curLabel} <ChevronRight size={10} className={`transition ${open ? "rotate-90" : ""}`}/>
      </button>
      {open && (
        <div className="absolute mt-2 left-0 bg-[#261534] border border-[#3D2845] rounded-xl py-1 min-w-[200px] z-40 shadow-2xl">
          <button onClick={() => set("self")}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-[#3D2845] ${cur === "self" ? "text-[#D4FF00]" : ""}`}>
            <Check size={12} className={`inline mr-1 ${cur === "self" ? "" : "opacity-0"}`}/>You
          </button>
          {profile.family_members.map(m => (
            <button key={m.member_id} onClick={() => set(m.member_id)}
              data-testid={`active-shopper-${m.member_id}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#3D2845] ${cur === m.member_id ? "text-[#D4FF00]" : ""}`}>
              <Check size={12} className={`inline mr-1 ${cur === m.member_id ? "" : "opacity-0"}`}/>
              {m.name} <span className="text-[#A1A1AA] text-xs">({m.relation})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
function OverviewTab({ profile, onUpdated, onError, onLogout }) {
  const isShopper = profile.role === "buyer";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: profile.name, phone: profile.phone,
    phone_cc: profile.phone_cc || "", phone_country: profile.phone_country || "",
    address: profile.address, address_obj: profile.address_obj || {},
    id_proof_url: profile.id_proof_url || "", tax_id: profile.tax_id || "",
    dob: profile.dob, gender: profile.gender, bio: profile.bio,
  });
  const [wallet, setWallet] = useState(null);
  useEffect(() => {
    if (!isShopper) return; // sellers/admins have no shopper wallet
    api.get("/wallet").then(({data}) => setWallet(data)).catch(()=>{});
  }, [isShopper]);
  const policy = useFieldPolicy("buyer");
  const pol = (k) => policyFor(policy, k);
  const requiredAddr = ["line1","city","country","postal_code","state","line2"]
    .filter(k => pol(`address_${k.replace("line","line")}`).required)
    .map(k => `address_${k}`);

  const save = async () => {
    try {
      await api.put("/buyer/profile", form);
      setEditing(false);
      onUpdated("Profile updated.");
    } catch (e) { onError(formatApiError(e?.response?.data?.detail) || "Save failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Personal info */}
      <div className="card p-5 lg:col-span-2 grid gap-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold uppercase text-sm flex items-center gap-2">
            <UserIcon size={14}/> Personal info
          </div>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="text-xs text-[#A1A1AA] hover:text-[#D4FF00]"
              data-testid="profile-edit-btn"><Edit2 size={12} className="inline mr-1"/>Edit</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); setForm({
                name: profile.name, phone: profile.phone,
                phone_cc: profile.phone_cc || "", phone_country: profile.phone_country || "",
                address: profile.address, address_obj: profile.address_obj || {},
                id_proof_url: profile.id_proof_url || "", tax_id: profile.tax_id || "",
                dob: profile.dob, gender: profile.gender, bio: profile.bio,
              }); }} className="text-xs text-[#A1A1AA] hover:text-[#FF007F]">
                <X size={12} className="inline mr-1"/>Cancel
              </button>
              <button onClick={save} data-testid="profile-save-btn" className="btn-primary text-xs px-3 py-1.5">
                <Save size={12} className="inline mr-1"/>Save
              </button>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {!pol("name").hidden &&
            <Field label="Name" value={form.name} edit={editing} required={pol("name").required}
              onChange={v => setForm({...form, name: v})} testid="profile-name"/>}
          {!pol("dob").hidden &&
            <Field label="Date of birth" value={form.dob} edit={editing} type="date" required={pol("dob").required}
              onChange={v => setForm({...form, dob: v})} testid="profile-dob"/>}
          {!pol("gender").hidden &&
            <Field label="Gender" value={form.gender} edit={editing} type="select" required={pol("gender").required}
              options={GENDERS.map(g => [g.id, g.label])}
              onChange={v => setForm({...form, gender: v})} testid="profile-gender"/>}
          {!pol("tax_id").hidden &&
            <Field label="Tax ID" value={form.tax_id} edit={editing} required={pol("tax_id").required}
              onChange={v => setForm({...form, tax_id: v})} testid="profile-tax-id"/>}
        </div>

        {!pol("phone").hidden && (
          <PhoneField
            phone={form.phone} phoneCountry={form.phone_country}
            editable={editing} required={pol("phone").required}
            onChange={(patch) => setForm({ ...form, ...patch })}
            testid="profile-phone"/>
        )}

        {!pol("address_obj").hidden && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-2">
              Address{pol("address_obj").required && <span className="text-[#FF007F] ml-1">*</span>}
            </div>
            <AddressFields value={form.address_obj} editable={editing}
              testid="profile-addr"
              requiredFields={requiredAddr}
              onChange={(addr) => setForm({ ...form, address_obj: addr })}/>
          </div>
        )}

        {!pol("id_proof_url").hidden && (
          <IdProofField value={form.id_proof_url} editable={editing}
            required={pol("id_proof_url").required}
            testid="profile-id-proof"
            onChange={(v) => setForm({ ...form, id_proof_url: v })}/>
        )}

        {!pol("bio").hidden &&
          <Field label="About me" value={form.bio} edit={editing} textarea required={pol("bio").required}
            onChange={v => setForm({...form, bio: v})} testid="profile-bio"/>}
      </div>

      {/* Wallet + nav */}
      <div className="space-y-3">
        {isShopper && wallet && (
          <div className="card p-5 grain relative overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Wallet size={16} className="text-[#D4FF00]"/>
                <div className="text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Wallet</div>
              </div>
              <Sparkles size={14} className="text-[#FF007F]"/>
            </div>
            <div className="font-display font-black text-4xl text-[#D4FF00]" data-testid="wallet-balance">{fmtMoney(wallet.balance)}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">Earn 5% back on every order.</div>
            {(wallet.transactions || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#3D2845] space-y-1 max-h-40 overflow-y-auto pr-1">
                {wallet.transactions.slice(0, 6).map(t => (
                  <div key={t.tx_id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {t.amount >= 0 ? <ArrowDownToLine size={11} className="text-[#D4FF00]"/>
                                     : <ArrowUpFromLine size={11} className="text-[#FF007F]"/>}
                      <div className="truncate text-[#A1A1AA]">{t.note}</div>
                    </div>
                    <div className={`font-bold ${t.amount >= 0 ? "text-[#D4FF00]" : "text-[#FF007F]"}`}>
                      {t.amount >= 0 ? "+" : "-"}{fmtMoney(Math.abs(t.amount))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {isShopper && (
          <Link to="/orders" className="card p-3 flex items-center gap-3 text-sm hover:border-[#D4FF00] transition">
            <Package size={16} className="text-[#D4FF00]"/> My orders <ChevronRight size={14} className="ml-auto text-[#A1A1AA]"/>
          </Link>
        )}
        {profile.role === "seller" && (
          <Link to="/seller" className="card p-3 flex items-center gap-3 text-sm hover:border-[#D4FF00] transition">
            <Store size={16}/> Seller dashboard <ChevronRight size={14} className="ml-auto text-[#A1A1AA]"/>
          </Link>
        )}
        {profile.role === "admin" && (
          <Link to="/admin" className="card p-3 flex items-center gap-3 text-sm hover:border-[#FF007F] transition">
            <Shield size={16} className="text-[#FF007F]"/> Admin console <ChevronRight size={14} className="ml-auto text-[#A1A1AA]"/>
          </Link>
        )}
        <button onClick={onLogout} data-testid="profile-logout"
          className="card p-3 flex items-center gap-3 text-sm text-[#FF007F] w-full hover:border-[#FF007F]">
          <LogOut size={16}/> Logout <ChevronRight size={14} className="ml-auto"/>
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, edit, onChange, type = "text", options, testid, textarea, required }) {
  return (
    <div className="grid gap-1">
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">
        {label}{required && <span className="text-[#FF007F] ml-0.5">*</span>}
      </label>
      {!edit ? (
        <div className="text-sm text-[#FAFAFA] min-h-[20px]" data-testid={testid}>
          {label === "Gender" && value ? (GENDERS.find(g => g.id === value)?.label || value) : (value || "—")}
        </div>
      ) : type === "select" ? (
        <select className="input-dark" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : textarea ? (
        <textarea className="input-dark min-h-[60px]" value={value || ""}
          onChange={e => onChange(e.target.value)} data-testid={testid}/>
      ) : (
        <input type={type} className="input-dark" value={value || ""}
          onChange={e => onChange(e.target.value)} data-testid={testid}/>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
function SavedTab() {
  const links = [
    { to: "/saved/products", icon: Heart, label: "Saved products", color: "#D4FF00", testid: "saved-products-link" },
    { to: "/saved/videos", icon: Film, label: "Saved videos", color: "#00F0FF", testid: "saved-videos-link" },
    { to: "/saved/streams", icon: Radio, label: "Saved live streams", color: "#FF007F", testid: "saved-streams-link" },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {links.map(l => (
        <Link key={l.to} to={l.to} data-testid={l.testid}
          className="card p-5 hover:border-[#D4FF00] group transition relative overflow-hidden">
          <l.icon size={28} style={{ color: l.color }}/>
          <div className="font-display font-black text-xl mt-2">{l.label}</div>
          <div className="text-xs text-[#A1A1AA] mt-1">View all</div>
          <ChevronRight size={14} className="absolute top-5 right-5 text-[#A1A1AA] group-hover:translate-x-1 transition"/>
        </Link>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
export function PreferencesTab({ profile, schema, initialOwner, onUpdated, onError, categoryFilter }) {
  const [owner, setOwner] = useState(initialOwner || "self");
  useEffect(() => { if (initialOwner) setOwner(initialOwner); }, [initialOwner]);
  const ownerLabel = owner === "self" ? "You"
    : (profile.family_members.find(m => m.member_id === owner)?.name || "Member");
  const ownerPrefs = (profile.preferences || {})[owner] || {};
  const ownerEnabled = (profile.preferences_enabled || {})[owner] || {};
  const isEnabled = (catId) => ownerEnabled[catId] !== false; // default true
  const schemaEntries = Object.entries(schema).filter(
    ([catId]) => !categoryFilter || catId === categoryFilter
  );

  return (
    <div className="grid gap-4">
      {/* Picker */}
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mr-2">Setting preferences for:</div>
        <button onClick={() => setOwner("self")} data-testid="pref-owner-self"
          className={`chip text-xs ${owner === "self" ? "active" : ""}`}>You</button>
        {profile.family_members.map(m => (
          <button key={m.member_id} onClick={() => setOwner(m.member_id)}
            data-testid={`pref-owner-${m.member_id}`}
            className={`chip text-xs ${owner === m.member_id ? "active" : ""}`}>
            {m.name} <span className="text-[10px] text-[#A1A1AA]">({m.relation})</span>
          </button>
        ))}
        <div className="text-xs text-[#A1A1AA] ml-auto">{Object.keys(ownerPrefs).length} categories set</div>
      </div>

      <div className="text-xs text-[#A1A1AA]">
        Tip: pick multiple options per row (e.g. sizes "L" + "XL"), and toggle a row off if you want to ignore those preferences temporarily without losing them. Recommendations on the home page use the active picks for {ownerLabel}.
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="pref-table">
          <thead className="bg-[#3D2845] text-left">
            <tr>
              <th className="p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA] sticky left-0 bg-[#3D2845]">Category</th>
              <th className="p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA]">Your picks</th>
              <th className="p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA]">Active</th>
              <th className="p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {schemaEntries.map(([catId, def]) => (
              <PrefTableRow key={catId + owner} catId={catId} def={def} owner={owner}
                current={ownerPrefs[catId] || {}}
                enabled={isEnabled(catId)}
                onSaved={onUpdated} onError={onError}/>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PrefTableRow({ catId, def, owner, current, enabled, onSaved, onError }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(current);
  const [busy, setBusy] = useState(false);
  const [localEnabled, setLocalEnabled] = useState(enabled);
  useEffect(() => { setForm(current); }, [current, owner]);
  useEffect(() => { setLocalEnabled(enabled); }, [enabled, owner]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasPicks = useMemo(
    () => Object.values(current).some(v => v != null && v !== "" && (!Array.isArray(v) || v.length > 0)),
    [current]
  );

  const summary = useMemo(() => {
    const parts = [];
    for (const f of def.fields) {
      const v = current[f.key];
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      const display = Array.isArray(v) ? v.join(", ") : v;
      parts.push(<span key={f.key} className={`chip text-[10px] ${localEnabled ? "active" : "opacity-50"}`}>{f.label}: {display}</span>);
    }
    return parts.length ? parts : [<span key="ns" className="text-xs text-[#A1A1AA]">— Not set —</span>];
  }, [current, def, localEnabled]);

  const cleanedPayload = () => {
    const out = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === "" || v == null) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      out[k] = v;
    }
    return out;
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/buyer/preferences", { owner, category_id: catId, preferences: cleanedPayload() });
      onSaved(`${def.label} saved.`);
      setOpen(false);
    } catch (e) { onError(formatApiError(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true); setForm({});
    try {
      await api.put("/buyer/preferences", { owner, category_id: catId, preferences: {} });
      onSaved(`${def.label} cleared.`);
    } catch (e) { onError(formatApiError(e?.response?.data?.detail) || "Clear failed"); }
    finally { setBusy(false); }
  };
  const toggleEnabled = async () => {
    if (!hasPicks) {
      onError("Add at least one preference before toggling on/off.");
      return;
    }
    const next = !localEnabled;
    setLocalEnabled(next);
    try {
      await api.put("/buyer/preferences/toggle",
        { owner, category_id: catId, enabled: next });
      onSaved(`${def.label} ${next ? "activated" : "deactivated"}.`);
    } catch (e) {
      setLocalEnabled(!next);
      onError(formatApiError(e?.response?.data?.detail) || "Toggle failed");
    }
  };

  return (
    <>
      <tr className={`border-t border-[#3D2845] hover:bg-[#3D2845]/30 ${!localEnabled && hasPicks ? "opacity-70" : ""}`}
        data-testid={`pref-row-${catId}`}>
        <td className="p-3 align-top sticky left-0 bg-[#0F0817]">
          <div className="font-display font-bold uppercase text-sm whitespace-nowrap">{def.label}</div>
          <div className="text-[10px] text-[#A1A1AA] mt-1">{def.fields.length} fields</div>
        </td>
        <td className="p-3">
          <div className="flex flex-wrap gap-1.5">{summary}</div>
        </td>
        <td className="p-3 align-top">
          <button onClick={toggleEnabled}
            data-testid={`pref-toggle-${catId}`}
            disabled={!hasPicks}
            aria-pressed={localEnabled}
            title={hasPicks ? (localEnabled ? "Deactivate this category" : "Activate this category") : "Add picks first"}
            className={`relative inline-flex items-center h-6 w-11 rounded-full transition ${
              !hasPicks ? "bg-[#1F1F22] cursor-not-allowed"
                        : localEnabled ? "bg-[#D4FF00]" : "bg-[#3D2845]"
            }`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-transform ${
              localEnabled && hasPicks ? "translate-x-[22px] bg-black" : "translate-x-0.5 bg-[#A1A1AA]"
            }`}/>
          </button>
          <div className={`text-[10px] mt-1 font-bold uppercase tracking-wider ${
            !hasPicks ? "text-[#52525B]" : localEnabled ? "text-[#D4FF00]" : "text-[#A1A1AA]"
          }`}>
            {!hasPicks ? "—" : localEnabled ? "On" : "Off"}
          </div>
        </td>
        <td className="p-3 text-right">
          <button onClick={() => setOpen(o => !o)} data-testid={`pref-row-toggle-${catId}`}
            className="btn-secondary text-xs px-3 py-1.5">
            {open ? <X size={12} className="inline mr-1"/> : <Edit2 size={12} className="inline mr-1"/>}
            {open ? "Close" : "Edit"}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-[#0F0F11] border-t border-[#3D2845]" data-testid={`pref-edit-${catId}`}>
          <td colSpan={4} className="p-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {def.fields.map(f => (
                <PrefField key={f.key} field={f} value={form[f.key]}
                  onChange={v => setField(f.key, v)}
                  testid={`pref-${catId}-${f.key}`}/>
              ))}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={clear} disabled={busy} className="btn-secondary text-xs px-3 py-2"
                data-testid={`pref-clear-${catId}`}>Clear</button>
              <button onClick={() => setOpen(false)} disabled={busy} className="btn-secondary text-xs px-3 py-2">Cancel</button>
              <button onClick={save} disabled={busy} className="btn-primary text-xs px-4 py-2"
                data-testid={`pref-save-${catId}`}>
                <Save size={12} className="inline mr-1"/>{busy ? "Saving…" : "Save"}
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PrefField({ field, value, onChange, testid }) {
  if (field.type === "select") {
    return (
      <div className="grid gap-1">
        <label className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">{field.label}</label>
        <select className="input-dark" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}>
          <option value="">—</option>
          {field.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <div className="grid gap-1">
        <label className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">{field.label}</label>
        <input type="number" className="input-dark" value={value ?? ""}
          onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid}/>
      </div>
    );
  }
  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o]);
    return (
      <div className="grid gap-1">
        <label className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">{field.label}</label>
        <div className="flex flex-wrap gap-2">
          {field.options.map(o => (
            <button key={o} onClick={() => toggle(o)} type="button"
              data-testid={`${testid}-${o}`}
              className={`chip text-xs ${arr.includes(o) ? "active" : ""}`}>{o}</button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "tags") {
    return <TagsField field={field} value={value} onChange={onChange} testid={testid}/>;
  }
  return null;
}

function TagsField({ field, value, onChange, testid }) {
  const arr = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = () => { if (draft.trim()) { onChange([...arr, draft.trim()]); setDraft(""); } };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  return (
    <div className="grid gap-1">
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold">{field.label}</label>
      <div className="flex flex-wrap gap-2">
        {arr.map((t, i) => (
          <span key={`${t}-${i}`} className="chip active text-xs gap-1">
            {t} <button type="button" onClick={() => remove(i)} className="ml-1 text-[#FF007F]"><X size={10}/></button>
          </span>
        ))}
        <input className="input-dark py-1 text-xs flex-1 min-w-[140px]" placeholder="Add brand and press Enter"
          value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={testid}/>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
function FamilyTab({ profile, onUpdated, onError, onPrefer }) {
  const [editing, setEditing] = useState(null); // {member_id?, name, relation, gender, age}
  const blank = { name: "", relation: "mom", gender: "prefer_not", age: "" };

  const save = async () => {
    if (!editing.name) { onError("Name is required"); return; }
    try {
      const payload = {
        member_id: editing.member_id,
        name: editing.name,
        relation: editing.relation,
        gender: editing.gender || "prefer_not",
        age: editing.age === "" ? null : Number(editing.age),
      };
      await api.post("/buyer/family", payload);
      setEditing(null);
      onUpdated(editing.member_id ? "Member updated." : "Member added.");
    } catch (e) { onError(formatApiError(e?.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (mid) => {
    if (!window.confirm("Remove this member and their preferences?")) return;
    try {
      await api.delete(`/buyer/family/${mid}`);
      onUpdated("Member removed.");
    } catch (e) { onError(formatApiError(e?.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="card p-4 flex items-center justify-between">
        <div>
          <div className="font-display font-bold uppercase text-sm">Family members</div>
          <div className="text-xs text-[#A1A1AA] mt-1">Add the people you shop for. Each can have their own size + style preferences.</div>
        </div>
        {!editing && (
          <button onClick={() => setEditing(blank)} data-testid="family-add-btn"
            className="btn-primary text-xs px-3 py-2"><Plus size={12} className="inline mr-1"/>Add member</button>
        )}
      </div>

      {editing && (
        <div className="card p-4 grid gap-3 border-2 border-[#D4FF00]/40" data-testid="family-edit-card">
          <div className="font-display font-bold uppercase text-sm">{editing.member_id ? "Edit member" : "New family member"}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="input-dark" placeholder="Full name" value={editing.name}
              onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="family-name-input"/>
            <select className="input-dark" value={editing.relation}
              onChange={e => setEditing({ ...editing, relation: e.target.value })} data-testid="family-relation-input">
              {RELATIONS.filter(r => r !== "self").map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="input-dark" value={editing.gender}
              onChange={e => setEditing({ ...editing, gender: e.target.value })} data-testid="family-gender-input">
              {GENDERS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
            <input type="number" min={0} max={120} className="input-dark" placeholder="Age"
              value={editing.age} onChange={e => setEditing({ ...editing, age: e.target.value })}
              data-testid="family-age-input"/>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="btn-secondary text-xs px-3 py-2"
              data-testid="family-cancel-btn">Cancel</button>
            <button onClick={save} className="btn-primary text-xs px-4 py-2"
              data-testid="family-save-btn"><Save size={12} className="inline mr-1"/>Save</button>
          </div>
        </div>
      )}

      {profile.family_members.length === 0 && !editing && (
        <div className="card p-8 text-center text-[#A1A1AA]">
          No family members yet. Add one to set their preferences and shop on their behalf.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {profile.family_members.map(m => (
          <div key={m.member_id} className="card p-4 flex items-center gap-3" data-testid={`family-member-${m.member_id}`}>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4FF00] to-[#00F0FF] flex items-center justify-center text-black font-display font-black">
              {m.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{m.name}</div>
              <div className="text-xs text-[#A1A1AA] capitalize">{m.relation} · {m.age || "—"}y · {m.gender?.replace("_", " ")}</div>
              {(() => {
                const setCats = Object.keys((profile.preferences || {})[m.member_id] || {}).length;
                return setCats > 0 ? (
                  <div className="text-[10px] mt-1 text-[#D4FF00] font-bold">
                    <Sparkles size={10} className="inline mr-1"/> {setCats} preference{setCats === 1 ? "" : "s"} set
                  </div>
                ) : null;
              })()}
            </div>
            <button onClick={() => onPrefer(m.member_id)}
              data-testid={`family-prefer-${m.member_id}`}
              title="Set preferences"
              className="w-8 h-8 rounded-lg bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black flex items-center justify-center">
              <Sparkles size={12}/>
            </button>
            <button onClick={() => setEditing({ ...m, age: m.age ?? "" })}
              data-testid={`family-edit-${m.member_id}`}
              className="w-8 h-8 rounded-lg bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black flex items-center justify-center">
              <Edit2 size={12}/>
            </button>
            <button onClick={() => remove(m.member_id)}
              data-testid={`family-remove-${m.member_id}`}
              className="w-8 h-8 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
              <Trash2 size={12}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
